import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import Swal from 'sweetalert2';

const FULFILLMENT_STATUS_ORDER = {
  pending: 0,
  sent_to_kitchen: 1,
  in_preparation: 2,
  ready: 3,
  served: 4,
  out_for_delivery: 5,
  complete: 6,
};

const statusOrder = (s) => FULFILLMENT_STATUS_ORDER[s || 'pending'] ?? 0;

const getMinFulfillmentStatus = (items) => {
  if (!items || items.length === 0) return 'pending';
  let minOrder = Infinity;
  let minStatus = 'complete';
  for (const item of items) {
    // Default missing fulfillmentStatus to sent_to_kitchen (not pending) so
    // the sale doc stays in the KDS active query and isn't accidentally dropped
    const s = item.fulfillmentStatus || 'sent_to_kitchen';
    const o = statusOrder(s);
    if (o < minOrder) {
      minOrder = o;
      minStatus = s;
    }
  }
  return minStatus;
};

const formatTimeAgo = (date) => {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : new Date(date);
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  const diffHours = Math.floor(diffMins / 60);
  return `${diffHours}h ${diffMins % 60}m`;
};

const formatTime = (date) => {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const KDSStationView = () => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [station, setStation] = useState(null);
  const [location, setLocation] = useState(null);
  const [sessionDoc, setSessionDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  // Orders map: saleId -> { sale, items: [...] }
  const [orders, setOrders] = useState({});
  const unsubscribers = useRef([]);

  // Completed logs from kdsSessions/{id}/completedLogs
  const [completedLogs, setCompletedLogs] = useState([]);
  const [completedLogsCount, setCompletedLogsCount] = useState(0);

  // View mode: 'active' (sent_to_kitchen / in_preparation) or 'completed'
  const [viewMode, setViewMode] = useState('active');

  // Action modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionTab, setActionTab] = useState('order'); // 'order' or 'items'
  const [processing, setProcessing] = useState(false);

  // Track when each order became all-ready (for 3-second removal delay)
  const readySinceRef = useRef({});
  const removalTimers = useRef({});
  const pendingRemovalRef = useRef({}); // delayed removal from sales-query cleanup

  // Kitchen abbreviations: productId → short name configured on the product.
  // Kitchen cards/tickets show the abbreviation instead of the full product
  // name when one exists. Loaded once per station view (near-static data).
  const [productAbbreviations, setProductAbbreviations] = useState({});

  const itemDisplayName = (item) => {
    const abbr = item?.productId ? productAbbreviations[item.productId] : null;
    return (abbr && String(abbr).trim()) || item?.productName || item?.name || 'Item';
  };

  // Auth load
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadStationData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  // Load the product catalog once to resolve kitchen abbreviations
  // (categories → products; the abbreviation field is optional per product).
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const db = getFirestore();
        const catsSnap = await getDocs(collection(db, 'users', user.uid, 'categories'));
        const map = {};
        for (const cat of catsSnap.docs) {
          const prodSnap = await getDocs(collection(db, 'users', user.uid, 'categories', cat.id, 'products'));
          prodSnap.docs.forEach((p) => {
            const abbr = p.data().abbreviation;
            if (abbr && String(abbr).trim()) map[p.id] = String(abbr).trim();
          });
        }
        setProductAbbreviations(map);
      } catch (e) {
        console.warn('[KDS] abbreviation catalog load failed (full names shown):', e.message);
      }
    })();
  }, [user]);

  // Cleanup all listeners on unmount
  useEffect(() => {
    return () => {
      unsubscribers.current.forEach((u) => u());
      unsubscribers.current = [];
      listenedKeys.current.clear();
      Object.values(removalTimers.current).forEach((t) => clearTimeout(t));
      removalTimers.current = {};
      Object.values(pendingRemovalRef.current).forEach((t) => clearTimeout(t));
      pendingRemovalRef.current = {};
    };
  }, []);

  // Auto-remove orders that have had no active items for > 3 seconds (active view only)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let changed = false;
      setOrders((prev) => {
        const next = { ...prev };
        Object.entries(next).forEach(([saleId, order]) => {
          const items = order.items || [];
          const hasActiveItem = items.some(
            (it) => ['sent_to_kitchen', 'in_preparation'].includes(it.fulfillmentStatus || 'pending')
          );
          if (hasActiveItem) {
            // Order is active — clear any removal timer
            delete readySinceRef.current[saleId];
          } else if (readySinceRef.current[saleId]) {
            // No active items and timer exists — remove after 3 seconds
            if (now - readySinceRef.current[saleId] > 3000) {
              delete next[saleId];
              delete readySinceRef.current[saleId];
              changed = true;
            }
          } else {
            // No active items and no timer — start one
            readySinceRef.current[saleId] = now;
          }
        });
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const loadStationData = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();

      // Load station
      const stationDoc = await getDoc(doc(db, 'users', uid, 'kds', stationId));
      if (!stationDoc.exists()) {
        Swal.fire('Error', 'KDS station not found', 'error');
        navigate('/admin/kds-stations');
        return;
      }
      const stationData = { id: stationDoc.id, ...stationDoc.data() };
      setStation(stationData);

      // Load location
      if (stationData.locationId) {
        const locDoc = await getDoc(doc(db, 'users', uid, 'locations', stationData.locationId));
        if (locDoc.exists()) {
          setLocation({ id: locDoc.id, ...locDoc.data() });
        }
      }

      // Reuse existing session from sessionStorage if available (survives reloads)
      let activeSessionId = null;
      let activeSessionData = null;
      try {
        const stored = sessionStorage.getItem(`activeKdsSession_${stationId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.sessionId) {
            const storedSessionDoc = await getDoc(doc(db, 'users', uid, 'kdsSessions', parsed.sessionId));
            if (storedSessionDoc.exists() && storedSessionDoc.data().status === 'open') {
              activeSessionId = parsed.sessionId;
              activeSessionData = { id: activeSessionId, ...storedSessionDoc.data() };
            }
          }
        }
      } catch (storageErr) {
        console.warn('Could not read stored KDS session:', storageErr.message);
      }

      if (!activeSessionId) {
        // Create new session
        const sessionRef = doc(collection(db, 'users', uid, 'kdsSessions'));
        const sessionData = {
          stationId: stationData.id,
          stationName: stationData.name || 'KDS',
          locationId: stationData.locationId,
          workzoneId: stationData.workzoneId,
          viewType: stationData.viewType || 'columns',
          startTime: new Date(),
          status: 'open',
          completedOrders: 0,
          totalOrders: 0,
        };
        await setDoc(sessionRef, sessionData);
        activeSessionId = sessionRef.id;
        activeSessionData = { id: activeSessionId, ...sessionData };
      }

      setSessionDoc(activeSessionData);
      try {
        sessionStorage.setItem(`activeKdsSession_${stationId}`, JSON.stringify({ sessionId: activeSessionId }));
      } catch (storageErr) {
        console.warn('Could not store KDS session:', storageErr.message);
      }

      // Ensure fresh listeners for every session load
      // This prevents stale listener keys from preventing new listeners when
      // a session is closed and reopened.
      unsubscribers.current.forEach((u) => u());
      unsubscribers.current = [];
      listenedKeys.current.clear();
      Object.values(removalTimers.current).forEach((t) => clearTimeout(t));
      removalTimers.current = {};
      Object.values(pendingRemovalRef.current).forEach((t) => clearTimeout(t));
      pendingRemovalRef.current = {};

      // Start listening for orders
      await startOrdersListener(uid, stationData);

      // Start listening for completed logs
      startCompletedLogsListener(uid, activeSessionId);
    } catch (e) {
      console.error('Error loading KDS station:', e);
      Swal.fire('Error', 'Failed to load KDS station', 'error');
    } finally {
      setLoading(false);
    }
  };

  const startOrdersListener = async (uid, stationData) => {
    if (listenedKeys.current.has('sales_query')) return;
    listenedKeys.current.add('sales_query');

    const db = getFirestore();
    const locId = stationData.locationId;
    const wzId = stationData.workzoneId;

    // Query orders within the configured time window.
    // IMPORTANT: We intentionally include 'complete' and 'served' in the
    // fulfillmentStatus filter. This ensures that if a sale doc's
    // fulfillmentStatus was incorrectly calculated (e.g., all items were once
    // marked complete but new items were later added with sent_to_kitchen), the
    // KDS will still find the sale and check its items. The item-level
    // filtering in listenToSaleItems will decide whether to actually display it.
    //
    // Performance safeguards:
    // - where('orderDate', '>=', startTime) => ignores old backlogs
    // - limit(100) => caps read cost; increased from 50 to reduce chance of
    //   missing active orders when many completed orders exist in the window
    // - Composite index required: (fulfillmentStatus ASC, orderDate ASC)
    const queryWindowHours = stationData.queryWindowHours || 12;
    const startTime = Timestamp.fromDate(new Date(Date.now() - queryWindowHours * 60 * 60 * 1000));
    console.log('[KDS] Starting orders listener. queryWindowHours=' + queryWindowHours, 'startTime=', startTime.toDate().toISOString());
    const salesQuery = query(
      collection(db, 'users', uid, 'locations', locId, 'sales'),
      where('fulfillmentStatus', 'in', ['pending', 'sent_to_kitchen', 'in_preparation', 'ready', 'served', 'complete']),
      where('orderDate', '>=', startTime),
      // descending so the 100-cap keeps the NEWEST orders (ascending pinned
      // the listener to the OLDEST 100 and new orders silently never showed
      // once >100 accumulated in the window — prod incident: froze at ~146).
      orderBy('orderDate', 'desc'),
      limit(100)
    );

    // Explicit one-time fetch to ensure open orders appear immediately,
    // even if the live listener has a cold-start delay or race condition.
    try {
      const initialSnap = await getDocs(salesQuery);
      console.log('[KDS] Initial fetch returned', initialSnap.docs.length, 'sale docs. Statuses:', initialSnap.docs.map(d => ({ id: d.id, fulfillmentStatus: d.data().fulfillmentStatus, status: d.data().status, orderDate: d.data().orderDate?.toDate?.().toISOString?.() })));
      initialSnap.docs.forEach((d) => {
        const saleData = { id: d.id, ...d.data() };
        listenToSaleItems(uid, locId, d.id, wzId, saleData);
      });
    } catch (fetchErr) {
      console.error('[KDS] Initial orders fetch failed:', fetchErr.message);
    }

    const unsub = onSnapshot(salesQuery, (snapshot) => {
      const saleIds = new Set();
      console.log('[KDS] onSnapshot received', snapshot.docs.length, 'sale docs. Changes:', snapshot.docChanges().map(c => ({ id: c.doc.id, type: c.type, fulfillmentStatus: c.doc.data().fulfillmentStatus })));
      snapshot.docs.forEach((d) => {
        const saleData = { id: d.id, ...d.data() };
        saleIds.add(d.id);
        // Cancel any pending removal for this sale (it reappeared in the query)
        if (pendingRemovalRef.current[d.id]) {
          clearTimeout(pendingRemovalRef.current[d.id]);
          delete pendingRemovalRef.current[d.id];
        }
        // For each sale, set up items listener
        listenToSaleItems(uid, locId, d.id, wzId, saleData);

        // Update sale data in orders state if already present (so counters/stats stay fresh)
        setOrders((prev) => {
          if (prev[d.id]) {
            return { ...prev, [d.id]: { ...prev[d.id], sale: saleData } };
          }
          return prev;
        });
      });

      // Delayed cleanup: only remove orders that have been missing from the
      // query for > 2 seconds. This prevents transient Firestore index flicker
      // (common with in + orderBy queries) from visually deleting orders.
      setOrders((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((sid) => {
          if (!saleIds.has(sid) && !readySinceRef.current[sid] && !pendingRemovalRef.current[sid]) {
            pendingRemovalRef.current[sid] = setTimeout(() => {
              setOrders((prev2) => {
                const next2 = { ...prev2 };
                delete next2[sid];
                return next2;
              });
              delete pendingRemovalRef.current[sid];
            }, 2000);
          }
        });
        return next;
      });
    }, (err) => {
      console.error('[KDS] Sales query listener error:', err.message);
    });

    unsubscribers.current.push(unsub);
  };

  const startCompletedLogsListener = (uid, sessionId) => {
    if (listenedKeys.current.has('completed_logs')) return;
    listenedKeys.current.add('completed_logs');

    const db = getFirestore();
    const logsQuery = collection(db, 'users', uid, 'kdsSessions', sessionId, 'completedLogs');

    const unsub = onSnapshot(logsQuery, (snapshot) => {
      const logs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCompletedLogs(logs);
      setCompletedLogsCount(logs.length);
    }, (err) => {
      console.error('[KDS] Completed logs listener error:', err.message);
    });

    unsubscribers.current.push(unsub);
  };

  const listenedKeys = useRef(new Set());

  const listenToSaleItems = (uid, locId, saleId, workzoneId, saleData) => {
    // Avoid duplicate listeners
    const existingKey = `items_${saleId}`;
    if (listenedKeys.current.has(existingKey)) return;
    listenedKeys.current.add(existingKey);

    const db = getFirestore();
    // IMPORTANT: We query ALL items in the sale (no workzoneId filter) and then
    // filter client-side. This ensures we don't miss items that were created
    // without a workzoneId (e.g. handheld cloud-mode orders where workzoneId
    // was not resolved before writing to Firestore).
    //
    // Client-side filtering keeps items that:
    //   - match this workzoneId, OR
    //   - have no workzoneId at all (null/undefined/empty string)
    //
    // This is slightly more data transfer than server-side filtering, but
    // items subcollections are small (< 50 docs) and correctness is more
    // important than the marginal read cost.
    const itemsQuery = collection(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items');

    const unsub = onSnapshot(itemsQuery, (itemsSnap) => {
      const allItems = itemsSnap.docs.map((d) => ({ _docId: d.id, id: d.id, ...d.data() }));
      const matchingItems = allItems.filter(
        (item) =>
          item.workzoneId === workzoneId ||
          item.workzoneId == null ||
          item.workzoneId === ''
      );

      // Active items: sent_to_kitchen or in_preparation
      const hasActiveItem = matchingItems.some(
        (item) =>
          ['sent_to_kitchen', 'in_preparation'].includes(
            item.fulfillmentStatus || 'pending'
          )
      );

      console.log('[KDS] Sale', saleId, 'allItems:', allItems.length, 'matchingItems:', matchingItems.length, 'hasActive:', hasActiveItem, 'workzoneId:', workzoneId);

      setOrders((prev) => {
        const next = { ...prev };
        if (hasActiveItem) {
          // Has active items — show in active view, clear any removal timer
          next[saleId] = { sale: saleData, items: matchingItems };
          delete readySinceRef.current[saleId];
        } else if (matchingItems.length > 0) {
          // Has matching items but none are active (ready, served, pending, etc.)
          // Keep temporarily; the 3-second interval will remove if still not active.
          next[saleId] = { sale: saleData, items: matchingItems };
          if (!readySinceRef.current[saleId]) {
            readySinceRef.current[saleId] = Date.now();
          }
        } else {
          // No matching items for this workzone — remove immediately
          delete next[saleId];
          delete readySinceRef.current[saleId];
        }
        return next;
      });
    }, (err) => {
      console.error('[KDS] Items listener error for sale', saleId, ':', err.message);
    });

    unsubscribers.current.push(unsub);
  };

  // Stats: Open and In Prep come from active orders; Completed comes from session log
  // CRITICAL: sort ascending (oldest first) — the listener query runs
  // orderDate DESC so the 100-cap keeps the NEWEST orders, but the kitchen
  // cooks/dispatches in ARRIVAL order. Without this the cards showed
  // newest-first (reported in prod after the listener direction fix).
  const visibleOrders = Object.values(orders).sort((a, b) => {
    const ad = a.sale?.orderDate?.toDate?.() || new Date(0);
    const bd = b.sale?.orderDate?.toDate?.() || new Date(0);
    return ad - bd;
  });
  const openOrders = visibleOrders.filter((o) => {
    const items = o.items || [];
    return items.some((it) => (it.fulfillmentStatus || 'pending') === 'sent_to_kitchen');
  });
  const inPrepItemCount = visibleOrders.reduce(
    (sum, o) =>
      sum + (o.items || []).filter((it) => (it.fulfillmentStatus || 'pending') === 'in_preparation').length,
    0
  );

  const handleCloseSession = async () => {
    if (!user || !sessionDoc) return;
    const result = await Swal.fire({
      title: 'Close Session?',
      text: 'Are you sure you want to close this KDS session?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Close',
    });
    if (!result.isConfirmed) return;

    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'users', user.uid, 'kdsSessions', sessionDoc.id), {
        status: 'closed',
        endTime: new Date(),
        completedOrders: completedLogsCount,
        totalOrders: visibleOrders.length,
      });
      // Clear sessionStorage so the next visit creates a fresh session
      try {
        sessionStorage.removeItem(`activeKdsSession_${stationId}`);
      } catch (storageErr) {
        console.warn('Could not clear stored KDS session:', storageErr.message);
      }
      // Clean up all listeners before navigating to prevent stale state
      unsubscribers.current.forEach((u) => u());
      unsubscribers.current = [];
      listenedKeys.current.clear();
      Object.values(removalTimers.current).forEach((t) => clearTimeout(t));
      removalTimers.current = {};
      Object.values(pendingRemovalRef.current).forEach((t) => clearTimeout(t));
      pendingRemovalRef.current = {};
      navigate('/admin/kds-stations');
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to close session', 'error');
    }
  };

  const handleOrderTap = (order) => {
    setSelectedOrder(order);
    setActionTab('order');
    setShowActionModal(true);
  };

  const logCompletedOrder = async (sale, items) => {
    if (!user || !sessionDoc) return;
    try {
      const db = getFirestore();
      const uid = user.uid;

      // Ensure we have items. If empty, read directly from the sale subcollection.
      let finalItems = items || [];
      if (!finalItems.length) {
        try {
          const locId = station?.locationId || sale.locationId;
          const saleId = sale.id;
          const itemsSnap = await getDocs(
            collection(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items')
          );
          finalItems = itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          console.warn('[KDS] logCompletedOrder fallback read', finalItems.length, 'items from sale subcollection');
        } catch (readErr) {
          console.warn('[KDS] Could not read sale items for log:', readErr.message);
        }
      }

      const logRef = doc(db, 'users', uid, 'kdsSessions', sessionDoc.id, 'completedLogs', sale.id);
      const logData = {
        saleId: sale.id,
        confirmationNumber: sale.confirmationNumber || sale.id?.substring(0, 8),
        customerName: sale.customerName || 'Walk-in',
        orderType: sale.orderType || 'Dine-in',
        guestNumber: sale.guestNumber || null,
        items: finalItems.map((it) => ({
          productId: it.productId || null,
          productName: it.productName || it.name || 'Item',
          qty: it.qty || 1,
          options: it.options || [],
          instructions: it.instructions || null,
          fulfillmentStatus: it.fulfillmentStatus || 'ready',
        })),
        completedAt: new Date(),
        stationId: station?.id || null,
        stationName: station?.name || 'KDS',
        workzoneId: station?.workzoneId || null,
      };
      await setDoc(logRef, logData, { merge: true });
    } catch (err) {
      console.warn('Failed to log completed order:', err.message);
    }
  };

  const updateItemStatus = async (order, item, newStatus) => {
    if (!user) return;
    setProcessing(true);
    try {
      const db = getFirestore();
      const uid = user.uid;
      const locId = station.locationId;
      const saleId = order.sale.id;
      const ts = new Date();
      const itemDocId = item._docId || item.id;
      if (!itemDocId) {
        throw new Error('Item has no Firestore document ID');
      }

      // Update item in location sale subcollection
      await setDoc(
        doc(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items', itemDocId),
        { fulfillmentStatus: newStatus, updatedAt: ts },
        { merge: true }
      );

      // Update item in user sale subcollection
      await setDoc(
        doc(db, 'users', uid, 'sales', saleId, 'items', itemDocId),
        { fulfillmentStatus: newStatus, updatedAt: ts },
        { merge: true }
      );

      // Recalculate min status from all items in location sale
      const refreshedItemsSnap = await getDocs(
        collection(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items')
      );
      const allItems = refreshedItemsSnap.docs.map((d) => ({ _docId: d.id, id: d.id, ...d.data() }));
      const minStatus = getMinFulfillmentStatus(allItems);

      // Update sale docs
      await updateDoc(doc(db, 'users', uid, 'locations', locId, 'sales', saleId), {
        fulfillmentStatus: minStatus,
        updatedAt: ts,
      });
      await setDoc(
        doc(db, 'users', uid, 'sales', saleId),
        { fulfillmentStatus: minStatus, updatedAt: ts },
        { merge: true }
      );

      // Bidirectional sync to open tab
      const openTabId = order.sale.openTabId || order.sale.id;
      if (openTabId) {
        try {
          const tabRef = doc(db, 'users', uid, 'openTabs', openTabId);
          const tabDoc = await getDoc(tabRef);
          if (tabDoc.exists()) {
            const tabData = tabDoc.data();
            const saleItemOptions = JSON.stringify(item.options || []);
            const saleItemInstructions = item.instructions || '';
            const tabItems = (tabData.items || []).map((it) => {
              const tabItemOptions = JSON.stringify(it.options || []);
              const tabItemInstructions = it.instructions || '';
              // Match by productId and disambiguate with options + instructions
              if (
                it.productId === item.productId &&
                tabItemOptions === saleItemOptions &&
                tabItemInstructions === saleItemInstructions
              ) {
                return { ...it, fulfillmentStatus: newStatus };
              }
              return it;
            });
            await updateDoc(tabRef, {
              items: tabItems,
              fulfillmentStatus: minStatus,
              updatedAt: ts,
            });
          }
        } catch (tabErr) {
          console.warn('Could not sync to open tab:', tabErr.message);
        }
      }

      // Dismiss modal after in_preparation or ready
      if (newStatus === 'in_preparation' || newStatus === 'ready') {
        setShowActionModal(false);
        setSelectedOrder(null);
      }

      // If newStatus is ready and this was the last workzone item, print ticket and log completion
      // Items with no workzoneId belong to ALL workzones
      if (newStatus === 'ready') {
        const wzItems = allItems.filter(
          (it) =>
            it.workzoneId === station.workzoneId ||
            it.workzoneId == null ||
            it.workzoneId === ''
        );
        const remainingUnreadyWZ = wzItems.filter(
          (it) => statusOrder(it.fulfillmentStatus || 'pending') < statusOrder('ready')
        );
        if (remainingUnreadyWZ.length === 0 && wzItems.length > 0) {
          printTicket(order.sale, wzItems);
          await logCompletedOrder(order.sale, wzItems);
        }
      }

      Swal.fire({
        title: 'Updated',
        text: `Item marked as ${newStatus.replace(/_/g, ' ')}`,
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: true,
      });
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to update item status', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const updateOrderStatus = async (order, newStatus) => {
    if (!user) return;
    setProcessing(true);
    try {
      const db = getFirestore();
      const uid = user.uid;
      const locId = station.locationId;
      const saleId = order.sale.id;
      const ts = new Date();

      // Update all matching items in location sale subcollection
      // Items with no workzoneId (null/empty) belong to ALL workzones
      const locItemsSnap = await getDocs(
        collection(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items')
      );
      const locBatch = [];
      const updatedItems = [];
      for (const d of locItemsSnap.docs) {
        const data = d.data();
        const matchesWorkzone =
          data.workzoneId === station.workzoneId ||
          data.workzoneId == null ||
          data.workzoneId === '';
        if (matchesWorkzone) {
          const itemStatus = data.fulfillmentStatus || 'pending';
          if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
            locBatch.push(
              setDoc(
                doc(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items', d.id),
                { fulfillmentStatus: newStatus, updatedAt: ts },
                { merge: true }
              )
            );
            updatedItems.push({ ...data, id: d.id, fulfillmentStatus: newStatus });
          }
        }
      }
      await Promise.all(locBatch);

      // Update all matching items in user sale subcollection (same workzone only)
      const userItemsSnap = await getDocs(
        collection(db, 'users', uid, 'sales', saleId, 'items')
      );
      const userBatch = [];
      for (const d of userItemsSnap.docs) {
        const data = d.data();
        const matchesWorkzone =
          data.workzoneId === station.workzoneId ||
          data.workzoneId == null ||
          data.workzoneId === '';
        if (matchesWorkzone) {
          const itemStatus = data.fulfillmentStatus || 'pending';
          if (statusOrder(itemStatus) <= statusOrder(newStatus)) {
            userBatch.push(
              setDoc(
                doc(db, 'users', uid, 'sales', saleId, 'items', d.id),
                { fulfillmentStatus: newStatus, updatedAt: ts },
                { merge: true }
              )
            );
          }
        }
      }
      await Promise.all(userBatch);

      // Recalculate min status from location items
      const refreshedLocItems = await getDocs(
        collection(db, 'users', uid, 'locations', locId, 'sales', saleId, 'items')
      );
      const allItems = refreshedLocItems.docs.map((d) => ({ _docId: d.id, id: d.id, ...d.data() }));
      const minStatus = getMinFulfillmentStatus(allItems);

      // Update sale docs
      await updateDoc(doc(db, 'users', uid, 'locations', locId, 'sales', saleId), {
        fulfillmentStatus: minStatus,
        updatedAt: ts,
      });
      await setDoc(
        doc(db, 'users', uid, 'sales', saleId),
        { fulfillmentStatus: minStatus, updatedAt: ts },
        { merge: true }
      );

      // Bidirectional sync to open tab
      const openTabId = order.sale.openTabId || order.sale.id;
      if (openTabId) {
        try {
          const tabRef = doc(db, 'users', uid, 'openTabs', openTabId);
          const tabDoc = await getDoc(tabRef);
          if (tabDoc.exists()) {
            const tabData = tabDoc.data();
            const tabItems = (tabData.items || []).map((it) => {
              const itStatus = it.fulfillmentStatus || 'pending';
              // Only sync items belonging to this workzone and at or before the new status
              if (it.workzoneId === station.workzoneId && statusOrder(itStatus) <= statusOrder(newStatus)) {
                return { ...it, fulfillmentStatus: newStatus };
              }
              return it;
            });
            await updateDoc(tabRef, {
              items: tabItems,
              fulfillmentStatus: minStatus,
              updatedAt: ts,
            });
          }
        } catch (tabErr) {
          console.warn('Could not sync to open tab:', tabErr.message);
        }
      }

      // Dismiss modal after in_preparation or ready
      if (newStatus === 'in_preparation' || newStatus === 'ready') {
        setShowActionModal(false);
        setSelectedOrder(null);
      }

      // If newStatus is ready, print ticket and log completion for this workzone only
      // Items with no workzoneId belong to ALL workzones
      if (newStatus === 'ready') {
        const wzItems = allItems.filter(
          (it) =>
            it.workzoneId === station.workzoneId ||
            it.workzoneId == null ||
            it.workzoneId === ''
        );
        printTicket(order.sale, wzItems);
        await logCompletedOrder(order.sale, wzItems);
      }

      Swal.fire({
        title: 'Updated',
        text: `Order marked as ${newStatus.replace(/_/g, ' ')}`,
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: true,
      });
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Failed to update order status', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const printTicket = (sale, items) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) return;

    const orderDate = sale.orderDate?.toDate ? sale.orderDate.toDate() : new Date(sale.orderDate);
    const itemsHtml = (items || [])
      .filter(
        (item) =>
          item.workzoneId === station.workzoneId ||
          item.workzoneId == null ||
          item.workzoneId === ''
      )
      .map(
        (item) => `
      <tr>
        <td style="padding:6px 0;font-size:18px;"><strong>${item.qty || 1}x</strong> ${itemDisplayName(item)}</td>
        <td style="padding:6px 0;text-align:right;font-size:16px;text-transform:capitalize;">${(item.fulfillmentStatus || 'pending').replace(/_/g, ' ')}</td>
      </tr>
      ${item.instructions ? `<tr><td colspan="2" style="font-size:14px;color:#555;padding:2px 0 6px 12px;"><em>Note: ${item.instructions}</em></td></tr>` : ''}
      ${item.options?.length ? `<tr><td colspan="2" style="font-size:14px;color:#777;padding:2px 0 6px 12px;">+ ${item.options.map((o) => o.itemName || o.name).join(', ')}</td></tr>` : ''}
    `
      )
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ticket #${sale.confirmationNumber || sale.id?.substring(0, 8) || ''}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 16px; padding: 20px; margin: 0; }
            h2 { margin: 0 0 12px; font-size: 24px; font-weight: 800; }
            .meta { margin-bottom: 16px; color: #555; font-size: 15px; line-height: 1.5; }
            table { width: 100%; border-collapse: collapse; }
            th { text-align: left; border-bottom: 2px dashed #000; padding-bottom: 6px; font-size: 16px; }
            td { border-bottom: 1px dotted #ccc; }
            .footer { margin-top: 20px; font-size: 13px; color: #888; }
          </style>
        </head>
        <body>
          <h2>ORDER #${sale.confirmationNumber || sale.id?.substring(0, 8) || ''}${sale.guestNumber ? ` · Guest #${sale.guestNumber}` : ''}</h2>
          <div class="meta">
            <strong>${sale.customerName || 'Walk-in'}</strong><br/>
            ${orderDate.toLocaleString()}<br/>
            ${sale.orderType || 'Dine-in'}
          </div>
          <table>
            <thead>
              <tr><th>Item</th><th style="text-align:right">Status</th></tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          <div class="footer">
            Printed: ${new Date().toLocaleString()}<br/>
            Station: ${station?.name || 'KDS'}
          </div>
          <script>window.onload = function() { window.print(); setTimeout(() => window.close(), 500); };</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const renderOrderCard = (order) => {
    const sale = order.sale;
    const items = order.items;
    const orderDate = sale.orderDate?.toDate ? sale.orderDate.toDate() : new Date(sale.orderDate);
    const elapsed = formatTimeAgo(orderDate);

    // Determine card color based on oldest item status
    const hasSentToKitchen = items.some(
      (item) => (item.fulfillmentStatus || 'pending') === 'sent_to_kitchen'
    );
    const allInPrepOrHigher = items.every(
      (item) => statusOrder(item.fulfillmentStatus || 'pending') >= statusOrder('in_preparation')
    );
    const allReady = items.every(
      (item) => statusOrder(item.fulfillmentStatus || 'pending') >= statusOrder('ready')
    );

    let cardBg = '#fff';
    let borderColor = '#e0e0e0';
    let statusLabel = 'New';
    if (allReady) {
      cardBg = '#e8f5e9';
      borderColor = '#4caf50';
      statusLabel = 'Ready';
    } else if (allInPrepOrHigher) {
      cardBg = '#e3f2fd';
      borderColor = '#2196f3';
      statusLabel = 'Preparing';
    } else if (hasSentToKitchen) {
      cardBg = '#fff8e1';
      borderColor = '#ffc107';
      statusLabel = 'New';
    }

    return (
      <div
        key={sale.id}
        onClick={() => handleOrderTap(order)}
        style={{
          background: cardBg,
          border: `2px solid ${borderColor}`,
          borderRadius: 12,
          padding: 16,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          height: '100%',
          overflow: 'hidden',
          transition: 'transform 0.1s',
          color: '#1a1a1a',
        }}
        onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
        onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#1a1a1a' }}>
            #{sale.confirmationNumber || sale.id?.substring(0, 8)}{sale.guestNumber ? ` · Guest #${sale.guestNumber}` : ''}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: borderColor }}>{statusLabel}</div>
        </div>
        <div style={{ fontSize: 16, color: '#444', fontWeight: 600 }}>
          {sale.customerName || 'Walk-in'} · {sale.orderType || 'Dine-in'} · {elapsed}
        </div>
        <div style={{ fontSize: 15, color: '#666' }}>
          {formatTime(orderDate)}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', marginTop: 8 }}>
          {items
            .filter((item) => (item.fulfillmentStatus || 'pending') !== 'pending')
            .sort((a, b) => {
              const order = { sent_to_kitchen: 0, in_preparation: 1, ready: 2 };
              return (order[a.fulfillmentStatus || 'pending'] ?? 99) - (order[b.fulfillmentStatus || 'pending'] ?? 99);
            })
            .map((item, idx, filteredItems) => {
              const itemStatus = item.fulfillmentStatus || 'pending';
              const isReady = itemStatus === 'ready';
              return (
                <div
                  key={item.id || idx}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: idx < filteredItems.length - 1 ? '1px dashed #ddd' : 'none',
                    fontSize: 18,
                    fontWeight: itemStatus === 'sent_to_kitchen' ? 800 : 600,
                    textDecoration: isReady ? 'line-through' : 'none',
                    opacity: isReady ? 0.5 : 1,
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 900 }}>{item.qty || 1}x</span>{' '}
                    {itemDisplayName(item)}
                    {item.instructions ? (
                      <div style={{ fontSize: 14, color: '#666', fontStyle: 'italic', marginTop: 2 }}>{item.instructions}</div>
                    ) : null}
                    {item.options?.length ? (
                      <div style={{ fontSize: 14, color: '#777', marginTop: 2 }}>
                        + {item.options.map((o) => o.itemName || o.name).join(', ')}
                      </div>
                    ) : null}
                    <div style={{ marginTop: 4 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          textTransform: 'capitalize',
                          letterSpacing: 0.5,
                          ...(itemStatus === 'sent_to_kitchen'
                            ? { background: '#FFC107', color: '#333' }
                            : itemStatus === 'in_preparation'
                            ? { background: '#2196F3', color: '#fff' }
                            : itemStatus === 'ready'
                            ? { background: '#4CAF50', color: '#fff' }
                            : { background: '#9E9E9E', color: '#fff' }),
                        }}
                      >
                        {itemStatus.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const renderEmptySlot = (index) => (
    <div
      key={`empty-${index}`}
      style={{
        background: '#222',
        border: '2px dashed #444',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 120,
        color: '#555',
        fontSize: 20,
        fontWeight: 700,
        gap: 10,
      }}
    >
      <i className="fas fa-utensils" style={{ fontSize: 40 }}></i>
      <span>Slot {index + 1}</span>
    </div>
  );

  const renderCompletedCard = (log) => {
    const completedAt = log.completedAt?.toDate ? log.completedAt.toDate() : new Date(log.completedAt);
    const items = log.items || [];
    return (
      <div
        key={log.id}
        style={{
          background: '#e8f5e9',
          border: '2px solid #4caf50',
          borderRadius: 12,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          height: '100%',
          overflow: 'hidden',
          color: '#1a1a1a',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#1a1a1a' }}>
            #{log.confirmationNumber || log.id?.substring(0, 8)}{log.guestNumber ? ` · Guest #${log.guestNumber}` : ''}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#4caf50' }}>Ready</div>
        </div>
        <div style={{ fontSize: 16, color: '#444', fontWeight: 600 }}>
          {log.customerName || 'Walk-in'} · {log.orderType || 'Dine-in'} · {formatTimeAgo(completedAt)}
        </div>
        <div style={{ fontSize: 15, color: '#666' }}>
          {formatTime(completedAt)}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', marginTop: 8 }}>
          {items.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: idx < items.length - 1 ? '1px dashed #ddd' : 'none',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              <div>
                <span style={{ fontWeight: 900 }}>{item.qty || 1}x</span>{' '}
                {itemDisplayName(item)}
                {item.instructions ? (
                  <div style={{ fontSize: 14, color: '#666', fontStyle: 'italic', marginTop: 2 }}>{item.instructions}</div>
                ) : null}
                {item.options?.length ? (
                  <div style={{ fontSize: 14, color: '#777', marginTop: 2 }}>
                    + {item.options.map((o) => o.itemName || o.name).join(', ')}
                  </div>
                ) : null}
                <div style={{ marginTop: 4 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      letterSpacing: 0.5,
                      ...(item.fulfillmentStatus === 'sent_to_kitchen'
                        ? { background: '#FFC107', color: '#333' }
                        : item.fulfillmentStatus === 'in_preparation'
                        ? { background: '#2196F3', color: '#fff' }
                        : item.fulfillmentStatus === 'ready'
                        ? { background: '#4CAF50', color: '#fff' }
                        : { background: '#9E9E9E', color: '#fff' }),
                    }}
                  >
                    {(item.fulfillmentStatus || 'ready').replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMainContent = () => {
    const viewType = station?.viewType || 'columns';

    // Completed view
    if (viewMode === 'completed') {
      const logList = completedLogs;
      if (viewType === 'columns') {
        const displayLogs = logList.slice(0, 5);
        const slots = [];
        for (let i = 0; i < 5; i++) {
          if (i < displayLogs.length) {
            slots.push(renderCompletedCard(displayLogs[i]));
          } else {
            slots.push(renderEmptySlot(i));
          }
        }
        return (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 12,
              height: '100%',
              padding: 12,
            }}
          >
            {slots}
          </div>
        );
      }
      const displayLogs = logList.slice(0, 10);
      const slots = [];
      for (let i = 0; i < 10; i++) {
        if (i < displayLogs.length) {
          slots.push(renderCompletedCard(displayLogs[i]));
        } else {
          slots.push(renderEmptySlot(i));
        }
      }
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: 12,
            height: '100%',
            padding: 12,
          }}
        >
          {slots}
        </div>
      );
    }

    // Active view: only show orders that have at least one active item
    // (sent_to_kitchen or in_preparation). Orders that are entirely ready
    // are hidden here — they appear in the Completed view instead.
    const orderList = visibleOrders.filter((o) =>
      (o.items || []).some((it) =>
        ['sent_to_kitchen', 'in_preparation'].includes(it.fulfillmentStatus || 'pending')
      )
    );

    if (viewType === 'columns') {
      // Columns view: always 5 columns, left to right, oldest to newest, one per column
      const displayOrders = orderList.slice(0, 5);
      const slots = [];
      for (let i = 0; i < 5; i++) {
        if (i < displayOrders.length) {
          slots.push(renderOrderCard(displayOrders[i]));
        } else {
          slots.push(renderEmptySlot(i));
        }
      }
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 12,
            height: '100%',
            padding: 12,
          }}
        >
          {slots}
        </div>
      );
    }

    // Grid view: always 5x2 grid, one order per cell
    const displayOrders = orderList.slice(0, 10);
    const slots = [];
    for (let i = 0; i < 10; i++) {
      if (i < displayOrders.length) {
        slots.push(renderOrderCard(displayOrders[i]));
      } else {
        slots.push(renderEmptySlot(i));
      }
    }
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gridTemplateRows: 'repeat(2, 1fr)',
          gap: 12,
          height: '100%',
          padding: 12,
        }}
      >
        {slots}
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1a1a1a',
        }}
      >
        <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 120, height: 120, objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#1a1a1a',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        color: '#fff',
      }}
    >
      {/* Top Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          background: '#2a2a2a',
          borderBottom: '1px solid #333',
          flexShrink: 0,
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 900 }}>{station?.name || 'KDS'}</div>
            <div style={{ fontSize: 15, color: '#888' }}>
              {location?.companyName || ''} · {station?.viewType === 'columns' ? 'Columns' : 'Grid'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <div
              onClick={() => setViewMode('completed')}
              style={{
                textAlign: 'center',
                padding: '8px 16px',
                background: viewMode === 'completed' ? '#1b3a1b' : '#333',
                borderRadius: 10,
                cursor: 'pointer',
                border: viewMode === 'completed' ? '2px solid #4caf50' : '2px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 14, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Completed</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#4caf50' }}>{completedLogsCount}</div>
            </div>
            <div
              onClick={() => setViewMode('active')}
              style={{
                textAlign: 'center',
                padding: '8px 16px',
                background: viewMode === 'active' ? '#3a3000' : '#333',
                borderRadius: 10,
                cursor: 'pointer',
                border: viewMode === 'active' ? '2px solid #ffc107' : '2px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 14, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>Open</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#ffc107' }}>{openOrders.length}</div>
            </div>
            <div
              onClick={() => setViewMode('active')}
              style={{
                textAlign: 'center',
                padding: '8px 16px',
                background: viewMode === 'active' ? '#0d2a3d' : '#333',
                borderRadius: 10,
                cursor: 'pointer',
                border: viewMode === 'active' ? '2px solid #2196f3' : '2px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 14, color: '#888', textTransform: 'uppercase', fontWeight: 700 }}>In Prep</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#2196f3' }}>{inPrepItemCount}</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ fontSize: 18, color: '#888', fontWeight: 600 }}>
            Session: {formatTime(sessionDoc?.startTime)}
          </div>
          <button
            onClick={handleCloseSession}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: '1px solid #555',
              background: '#333',
              color: '#fff',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-times mr-2"></i>Close Session
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>{renderMainContent()}</div>

      {/* Action Modal */}
      {showActionModal && selectedOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            style={{
              background: '#2a2a2a',
              borderRadius: 16,
              width: '100%',
              maxWidth: 560,
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: '1px solid #444',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #444',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: 26, fontWeight: 900 }}>
                  #{selectedOrder.sale.confirmationNumber || selectedOrder.sale.id?.substring(0, 8)}{selectedOrder.sale.guestNumber ? ` · Guest #${selectedOrder.sale.guestNumber}` : ''}
                </div>
                <div style={{ fontSize: 16, color: '#888', marginTop: 4 }}>
                  {selectedOrder.sale.customerName || 'Walk-in'} · {selectedOrder.sale.orderType || 'Dine-in'} · {formatTimeAgo(selectedOrder.sale.orderDate)}
                </div>
              </div>
              <button
                onClick={() => { setShowActionModal(false); setSelectedOrder(null); }}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  border: '1px solid #555',
                  background: '#333',
                  color: '#fff',
                  fontSize: 22,
                  cursor: 'pointer',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #444' }}>
              <button
                onClick={() => setActionTab('order')}
                style={{
                  flex: 1,
                  padding: 16,
                  background: actionTab === 'order' ? '#ff6b35' : 'transparent',
                  color: '#fff',
                  border: 'none',
                  fontSize: 18,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                By Order
              </button>
              <button
                onClick={() => setActionTab('items')}
                style={{
                  flex: 1,
                  padding: 16,
                  background: actionTab === 'items' ? '#ff6b35' : 'transparent',
                  color: '#fff',
                  border: 'none',
                  fontSize: 18,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                By Item
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              {actionTab === 'order' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ fontSize: 18, color: '#aaa', marginBottom: 8 }}>
                    Update all items in this order at once
                  </div>
                  {selectedOrder.items.some(
                    (item) => (item.fulfillmentStatus || 'pending') === 'sent_to_kitchen'
                  ) && (
                    <button
                      disabled={processing}
                      onClick={() => updateOrderStatus(selectedOrder, 'in_preparation')}
                      style={{
                        padding: '18px 0',
                        borderRadius: 14,
                        border: 'none',
                        background: '#2196f3',
                        color: '#fff',
                        fontSize: 20,
                        fontWeight: 900,
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.6 : 1,
                      }}
                    >
                      <i className="fas fa-fire mr-2"></i>Start Preparation
                    </button>
                  )}
                  {selectedOrder.items.some(
                    (item) => (item.fulfillmentStatus || 'pending') === 'in_preparation'
                  ) && (
                    <button
                      disabled={processing}
                      onClick={() => updateOrderStatus(selectedOrder, 'ready')}
                      style={{
                        padding: '18px 0',
                        borderRadius: 14,
                        border: 'none',
                        background: '#4caf50',
                        color: '#fff',
                        fontSize: 20,
                        fontWeight: 900,
                        cursor: processing ? 'not-allowed' : 'pointer',
                        opacity: processing ? 0.6 : 1,
                      }}
                    >
                      <i className="fas fa-check mr-2"></i>Mark All Ready
                    </button>
                  )}
                  {selectedOrder.items.every(
                    (item) => (item.fulfillmentStatus || 'pending') === 'ready'
                  ) && (
                    <div style={{ textAlign: 'center', color: '#4caf50', fontWeight: 900, fontSize: 22, padding: '16px 0' }}>
                      <i className="fas fa-check-circle mr-2"></i>Order is Ready
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {selectedOrder.items.map((item, idx) => {
                    const itemStatus = item.fulfillmentStatus || 'pending';
                    const canStartPrep = itemStatus === 'sent_to_kitchen';
                    const canMarkReady = itemStatus === 'in_preparation';
                    const isReady = itemStatus === 'ready';

                    return (
                      <div
                        key={item.id || idx}
                        style={{
                          padding: 16,
                          borderRadius: 12,
                          background: isReady ? '#1b3a1b' : '#333',
                          border: `1px solid ${isReady ? '#4caf50' : '#555'}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 20, fontWeight: 800 }}>
                            {item.qty || 1}x {itemDisplayName(item)}
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: 700,
                                textTransform: 'capitalize',
                                letterSpacing: 0.5,
                                ...(itemStatus === 'sent_to_kitchen'
                                  ? { background: '#FFC107', color: '#333' }
                                  : itemStatus === 'in_preparation'
                                  ? { background: '#2196F3', color: '#fff' }
                                  : itemStatus === 'ready'
                                  ? { background: '#4CAF50', color: '#fff' }
                                  : { background: '#9E9E9E', color: '#fff' }),
                              }}
                            >
                              {itemStatus.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          {canStartPrep && (
                            <button
                              disabled={processing}
                              onClick={() => updateItemStatus(selectedOrder, item, 'in_preparation')}
                              style={{
                                padding: '10px 16px',
                                borderRadius: 10,
                                border: 'none',
                                background: '#2196f3',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 800,
                                cursor: processing ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Start
                            </button>
                          )}
                          {canMarkReady && (
                            <button
                              disabled={processing}
                              onClick={() => updateItemStatus(selectedOrder, item, 'ready')}
                              style={{
                                padding: '10px 16px',
                                borderRadius: 10,
                                border: 'none',
                                background: '#4caf50',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 800,
                                cursor: processing ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Ready
                            </button>
                          )}
                          {isReady && (
                            <span style={{ fontSize: 18, color: '#4caf50', fontWeight: 900 }}>
                              <i className="fas fa-check"></i>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KDSStationView;
